/**
 * The last mile of every WagePro credentials sign-in — a password-only
 * `wageproAuth.login`, a `wageproTwoFactor.verifyLogin` code, or a
 * `wageproTwoFactor.completeEnrolment` — once the server has issued the
 * session. Extracted from `wagepro_login_screen.tsx` so the two-step screens
 * finish sign-in through exactly the same sequence instead of a copy of it.
 *
 * Collaborators are injected so the ordering is unit-testable without a
 * Capacitor bridge or a real navigation.
 */
import type { NativeSessionCarrier } from './native_shell_auth.js';

export interface CompleteSignInDeps {
  native: boolean;
  /**
   * Clear any stale WagePro-audience OAuth bearer token pair. If one were
   * left over from an earlier launch, `resolveAuthBoot`'s grant-first branch
   * (#24241) would consult it ahead of `wageproAuth.me` and boot the user
   * into whatever organisation the leftover token grants — not the one they
   * just signed in to.
   */
  clearBearerTokens: () => Promise<void>;
  /** `persistNativeSession` (`native_shell_auth.ts`), bound to the real store in production. */
  persistNativeSession: (
    result: NativeSessionCarrier,
    native: boolean,
  ) => Promise<boolean>;
  /** Navigate into the app. */
  enterApp: () => void;
}

export type CompleteSignInResult = 'entered' | 'session_not_stored';

/**
 * Clear stale bearer tokens, persist the native session (if any), then enter
 * the app. Resolves `'session_not_stored'` — WITHOUT navigating — when the
 * native shell could not keep the session: the store rejected, or the API
 * answered without a token. Navigating anyway would land the user in a 401
 * boot loop with no message.
 *
 * Persisting MUST complete before navigating: in the native shell the stored
 * token IS the session (the cross-site cookie is dropped), so navigating first
 * would race the very first `wageproAuth.me` of the new document against an
 * unwritten store.
 */
export async function completeWageproSignIn(
  result: NativeSessionCarrier,
  deps: CompleteSignInDeps,
): Promise<CompleteSignInResult> {
  await deps.clearBearerTokens();

  let persisted: boolean;
  try {
    persisted = await deps.persistNativeSession(result, deps.native);
  } catch (err) {
    // Local storage, not the network — never reported as a connection
    // problem (see the note in `wagepro_login_screen.tsx`).
    console.error('[wagepro-web] storing the native session failed:', err);
    persisted = false;
  }
  if (!persisted) return 'session_not_stored';

  deps.enterApp();
  return 'entered';
}
